/**
 * Sprint 2.8b Step 6 — useProgressData hook.
 *
 * The sole data-access layer for the Progress tab. Components do not call
 * APIs directly.
 *
 * React Query keys (all start with 'progress'):
 *   ['progress', 'audits', domainId]
 *   ['progress', 'audit', auditId]
 *   ['progress', 'window', domainId, from, to]
 *   ['progress', 'attributions', domainId, from, to]
 *   ['progress', 'citation-rate', domainId, atDate]
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DateRange {
  from: string;   // ISO 8601 date
  to: string;
  preset: '30d' | '90d' | 'all' | 'custom';
}

// Minimal typed shapes — enough for the components.
// Full spec types are in packages/agents/src/types/audit-results.ts
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AuditResults = Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Attribution = Record<string, any>;

// ---------------------------------------------------------------------------
// API helpers (delegated to api.progress.*)
// ---------------------------------------------------------------------------

function getProgressAudits(domainId: string) {
  return api.progress.audits(domainId);
}

function getProgressAudit(domainId: string, auditId: string) {
  return api.progress.audit(domainId, auditId);
}

function getProgressWindow(domainId: string, from: string, to: string) {
  return api.progress.window(domainId, from, to);
}

function getProgressAttributions(domainId: string, from: string, to: string) {
  return api.progress.attributions(domainId, from, to);
}

function getProgressCitationRate(domainId: string, at: string) {
  return api.progress.citationRate(domainId, at);
}

function getProgressSoMV(domainId: string, at: string) {
  return api.progress.shareOfModelVoice(domainId, at);
}

function getProgressHeadlineNarrative(domainId: string, from: string, to: string) {
  return api.progress.headlineNarrative(domainId, from, to);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

const THIRTY_SECONDS = 30_000;
const ONE_MINUTE = 60_000;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useProgressData(domainId: string): {
  status: 'loading' | 'success' | 'error';
  error?: { message: string; retry: () => void };
  thenAudit: AuditResults | null;
  nowAudit: AuditResults | null;
  windowAudits: AuditResults[];
  attributions: Attribution[];
  thenCitationRate: number | null;
  nowCitationRate: number | null;
  thenAiMentionShare: number | null;
  nowAiMentionShare: number | null;
  headlineNarrative: string | null;
  issuesFixed: number;
  visitsGained: number;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
} {
  // ── Audit list ──────────────────────────────────────────────────────────
  const auditsQuery = useQuery({
    queryKey: ['progress', 'audits', domainId],
    queryFn: () => getProgressAudits(domainId),
    staleTime: ONE_MINUTE,
    refetchInterval: () =>
      document.visibilityState === 'visible' ? THIRTY_SECONDS : false,
  });

  // ── Default date range: from first audit → today ─────────────────────
  const defaultFrom = auditsQuery.data?.audits[0]?.startedAt?.slice(0, 10) ?? today();

  const [dateRange, setDateRange] = useState<DateRange>({
    from: defaultFrom,
    to: today(),
    preset: 'all',
  });

  // When the first audit date loads for the first time, sync the 'from' if
  // the user hasn't changed the range manually (preset === 'all').
  const effectiveFrom =
    dateRange.preset === 'all' && auditsQuery.data?.audits.length
      ? (auditsQuery.data.audits[0]!.startedAt.slice(0, 10))
      : dateRange.from;

  const effectiveTo = dateRange.preset === 'all' ? today() : dateRange.to;

  // ── Resolve then/now audit IDs from the list ──────────────────────────
  const { thenAuditId, nowAuditId } = useMemo(() => {
    const audits = auditsQuery.data?.audits ?? [];
    if (audits.length === 0) return { thenAuditId: null, nowAuditId: null };

    // "then" = closest audit on or after `from`
    const thenAudit = audits.find((a) => a.startedAt.slice(0, 10) >= effectiveFrom) ?? audits[0]!;

    // "now" = closest audit on or before `to`
    const nowAudit =
      [...audits].reverse().find((a) => a.startedAt.slice(0, 10) <= effectiveTo) ??
      audits[audits.length - 1]!;

    return { thenAuditId: thenAudit.id, nowAuditId: nowAudit.id };
  }, [auditsQuery.data, effectiveFrom, effectiveTo]);

  // ── Individual audit queries ──────────────────────────────────────────
  const thenAuditQuery = useQuery({
    queryKey: ['progress', 'audit', thenAuditId],
    queryFn: () => getProgressAudit(domainId, thenAuditId!),
    enabled: thenAuditId !== null,
    staleTime: ONE_MINUTE,
    refetchInterval: () =>
      document.visibilityState === 'visible' ? THIRTY_SECONDS : false,
  });

  const nowAuditQuery = useQuery({
    queryKey: ['progress', 'audit', nowAuditId],
    queryFn: () => getProgressAudit(domainId, nowAuditId!),
    enabled: nowAuditId !== null && nowAuditId !== thenAuditId,
    staleTime: ONE_MINUTE,
    refetchInterval: () =>
      document.visibilityState === 'visible' ? THIRTY_SECONDS : false,
  });

  // ── Window audits (rollup chart) ──────────────────────────────────────
  const windowQuery = useQuery({
    queryKey: ['progress', 'window', domainId, effectiveFrom, effectiveTo],
    queryFn: () => getProgressWindow(domainId, effectiveFrom, effectiveTo),
    enabled: !!auditsQuery.data,
    staleTime: ONE_MINUTE,
    refetchInterval: () =>
      document.visibilityState === 'visible' ? THIRTY_SECONDS : false,
  });

  // ── Attributions ─────────────────────────────────────────────────────
  const attributionsQuery = useQuery({
    queryKey: ['progress', 'attributions', domainId, effectiveFrom, effectiveTo],
    queryFn: () => getProgressAttributions(domainId, effectiveFrom, effectiveTo),
    enabled: !!auditsQuery.data,
    staleTime: ONE_MINUTE,
    refetchInterval: () =>
      document.visibilityState === 'visible' ? THIRTY_SECONDS : false,
  });

  // ── Citation rates ────────────────────────────────────────────────────
  const thenCitationQuery = useQuery({
    queryKey: ['progress', 'citation-rate', domainId, effectiveFrom],
    queryFn: () => getProgressCitationRate(domainId, effectiveFrom),
    enabled: !!auditsQuery.data,
    staleTime: ONE_MINUTE,
  });

  const nowCitationQuery = useQuery({
    queryKey: ['progress', 'citation-rate', domainId, effectiveTo],
    queryFn: () => getProgressCitationRate(domainId, effectiveTo),
    enabled: !!auditsQuery.data,
    staleTime: ONE_MINUTE,
  });

  // ── Share of Model Voice ──────────────────────────────────────────────
  const thenSoMVQuery = useQuery({
    queryKey: ['progress', 'somv', domainId, effectiveFrom],
    queryFn: () => getProgressSoMV(domainId, effectiveFrom),
    enabled: !!auditsQuery.data,
    staleTime: ONE_MINUTE,
  });

  const nowSoMVQuery = useQuery({
    queryKey: ['progress', 'somv', domainId, effectiveTo],
    queryFn: () => getProgressSoMV(domainId, effectiveTo),
    enabled: !!auditsQuery.data,
    staleTime: ONE_MINUTE,
  });

  // ── Headline narrative ────────────────────────────────────────────────
  const headlineNarrativeQuery = useQuery({
    queryKey: ['progress', 'headline-narrative', domainId, effectiveFrom, effectiveTo],
    queryFn: () => getProgressHeadlineNarrative(domainId, effectiveFrom, effectiveTo),
    // Only fetch once we have attribution/audit data to build the narrative from
    enabled: !!auditsQuery.data && !attributionsQuery.isLoading,
    staleTime: ONE_MINUTE * 60, // narrative is Redis-cached 24h server-side
    retry: false, // graceful — the component falls back to a static narrative
  });

  // ── Aggregate status ──────────────────────────────────────────────────
  const allQueries = [
    auditsQuery,
    thenAuditQuery,
    nowAuditQuery,
    windowQuery,
    attributionsQuery,
    thenCitationQuery,
    nowCitationQuery,
    thenSoMVQuery,
    nowSoMVQuery,
    // headlineNarrativeQuery intentionally excluded — it's non-critical
  ];

  const hasError = allQueries.some((q) => q.isError);
  const isLoading =
    auditsQuery.isLoading ||
    (thenAuditId && thenAuditQuery.isLoading) ||
    (nowAuditId && nowAuditId !== thenAuditId && nowAuditQuery.isLoading) ||
    windowQuery.isLoading ||
    attributionsQuery.isLoading;

  const firstError = allQueries.find((q) => q.isError)?.error as Error | undefined;

  const status: 'loading' | 'success' | 'error' = hasError
    ? 'error'
    : isLoading
    ? 'loading'
    : 'success';

  // If the same audit covers both then and now (e.g. only one audit exists)
  const resolvedNowAudit =
    nowAuditId === thenAuditId
      ? (thenAuditQuery.data ?? null)
      : (nowAuditQuery.data ?? null);

  const errorProp =
    hasError && firstError
      ? {
          message: firstError.message,
          retry: () => allQueries.forEach((q) => void q.refetch()),
        }
      : undefined;

  // ── Computed stats from attributions ──────────────────────────────────
  const resolvedAttributions = attributionsQuery.data?.attributions ?? [];
  const issuesFixed = resolvedAttributions.length;
  const visitsGained = resolvedAttributions
    .filter((a: Attribution) => (a as Record<string, unknown>)['state'] === 'confirmed')
    .reduce((sum: number, a: Attribution) => {
      const visits = (a as Record<string, unknown>)['visits'] as { delta?: number } | undefined;
      return sum + (visits?.delta ?? 0);
    }, 0);

  return {
    status,
    ...(errorProp !== undefined ? { error: errorProp } : {}),
    thenAudit: thenAuditQuery.data ?? null,
    nowAudit: resolvedNowAudit,
    windowAudits: windowQuery.data?.audits ?? [],
    attributions: resolvedAttributions,
    thenCitationRate: thenCitationQuery.data?.rate ?? null,
    nowCitationRate: nowCitationQuery.data?.rate ?? null,
    thenAiMentionShare: thenSoMVQuery.data?.shareOfModelVoice ?? null,
    nowAiMentionShare: nowSoMVQuery.data?.shareOfModelVoice ?? null,
    headlineNarrative: headlineNarrativeQuery.data?.narrative ?? null,
    issuesFixed,
    visitsGained,
    dateRange: { ...dateRange, from: effectiveFrom, to: effectiveTo },
    setDateRange,
  };
}
